import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from '@strivelabs/ui-core';
import { ProcessingStatus, useKeyValList } from '@/hooks/useKeyValOperations';
import { processBucketRows } from '@/utils/bucketProcessor';
import { getBucketConfig } from '@/constants/contentRefreshBucketRegistry';
import { POLLING_INTERVAL } from '@/constants';
import { log } from '@/lib/logger';
import type { BlogPostRow, BlogPostRowWithStatus } from '@/components/content-refresh/types';

// Delay before starting polling to avoid immediate double-fetch on mount
const POLLING_START_DELAY = 1000;

export interface ContentRefreshDataConfig {
  workflowInstanceId: string;
  agentShortName: string;
  pollingInterval?: number;
  enabled?: boolean;
  /** Control fetch execution (defaults to true). Set to false to defer fetching until store phase completes. */
  shouldFetch?: boolean;
}

export interface ContentRefreshDataResult {
  data: BlogPostRowWithStatus[];
  isLoading: boolean;
  isError: boolean;
}

export function useContentRefreshData({
  workflowInstanceId,
  agentShortName,
  pollingInterval = POLLING_INTERVAL,
  enabled = true,
  shouldFetch = true,
}: ContentRefreshDataConfig): ContentRefreshDataResult {
  const [data, setData] = useState<BlogPostRowWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const { listBucket } = useKeyValList();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const bucketConfig = getBucketConfig(agentShortName);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!workflowInstanceId || !bucketConfig) return;
    if (isInitialLoad) setIsLoading(true);

    try {
      const [todoRows, inProgressRows, waitingForApprovalRows, completedRows, failedRows] = await Promise.all([
        listBucket(bucketConfig.todoBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.inProgressBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.waitingForApprovalBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.completedBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.failedBucket, workflowInstanceId, bucketConfig.appName),
      ]);

      const allRows: BlogPostRowWithStatus[] = [];
      const rowIdRef = { current: 0 };

      allRows.push(...processBucketRows<BlogPostRow>(completedRows, ProcessingStatus.COMPLETED, rowIdRef));
      allRows.push(...processBucketRows<BlogPostRow>(waitingForApprovalRows, ProcessingStatus.WAITING_FOR_APPROVAL, rowIdRef));
      allRows.push(...processBucketRows<BlogPostRow>(inProgressRows, ProcessingStatus.IN_PROGRESS, rowIdRef));
      allRows.push(...processBucketRows<BlogPostRow>(todoRows, ProcessingStatus.PENDING, rowIdRef));
      allRows.push(...processBucketRows<BlogPostRow>(failedRows, ProcessingStatus.FAILED, rowIdRef));

      log('info', 'Successfully fetched content refresh data from all key-value store buckets', {
        totalRows: allRows.length,
      });

      setData(allRows);
      setIsError(false);
    } catch (error) {
      log('error', 'Failed to fetch content refresh data', { error });
      setIsError(true);
      // Only show toast on initial load, not during polling
      if (isInitialLoad) {
        toast({
          title: "Error",
          description: "Failed to load content refresh data. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [workflowInstanceId, bucketConfig, listBucket]);

  useEffect(() => {
    if (!enabled || !shouldFetch || !workflowInstanceId || !bucketConfig) {
      setIsLoading(false);
      return;
    }

    fetchData(true);

    const startTimer = setTimeout(() => {
      pollingIntervalRef.current = setInterval(() => fetchData(false), pollingInterval);
    }, POLLING_START_DELAY);

    return () => {
      clearTimeout(startTimer);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [enabled, shouldFetch, workflowInstanceId, bucketConfig, fetchData, pollingInterval]);

  return { data, isLoading, isError };
}
